import React, { useMemo, useRef, createContext, use, useCallback } from 'react'
import { localStorageKeys } from '@sketch/constants'
import * as sessionStorage from '@sketch/utils/src/sessionStorage'

type LayerTreeCollapsedState = {
  collapsedElements: string[] // string values serialized as strings
}

type LayerTreeStateByKey = Partial<Record<string, LayerTreeCollapsedState>>

function deserializeCollapsedElements(
  stored: LayerTreeCollapsedState | undefined
): Set<string> {
  return stored?.collapsedElements
    ? new Set(stored.collapsedElements)
    : new Set()
}

function readFromStorage<T>(key: string): T | undefined {
  return sessionStorage.getParsedItem<LayerTreeStateByKey>(
    localStorageKeys.layerTreeStateByKey
  )?.[key] as T | undefined
}

function writeToStorage(key: string, value: LayerTreeCollapsedState) {
  const all =
    sessionStorage.getParsedItem<LayerTreeStateByKey>(
      localStorageKeys.layerTreeStateByKey
    ) ?? {}

  sessionStorage.setStringifiedItem(localStorageKeys.layerTreeStateByKey, {
    ...all,
    [key]: value,
  })
}

const Provider = createContext({
  getScrollOffset: (() => undefined) as () => number | undefined,
  setScrollOffset: (() => {}) as (value: number) => void,
  getCollapsedElements: (() => undefined) as () => Set<string> | undefined,
  setCollapsedElements: (() => {}) as (value: Set<string>) => void,
})

type LayerTreePersistentStateProps = {
  children: React.ReactNode
  /**
   * Key scoped to share + version + page. Used to persist collapsed elements,
   * which are shared between the page canvas and frame views.
   */
  collapsedStateKey: string
}

/**
 * Persists layer tree collapsed state across mount/unmount cycles and page reloads.
 * State is stored in sessionStorage and uses a ref as an in-memory cache to avoid
 * hitting storage on every read.
 */
export const LayerTreePersistentState = ({
  children,
  collapsedStateKey,
}: LayerTreePersistentStateProps) => {
  const scrollOffsetRef = useRef<number | undefined>(undefined)

  const getScrollOffset = useCallback(() => scrollOffsetRef.current, [])

  const setScrollOffset = useCallback((value: number) => {
    scrollOffsetRef.current = value
  }, [])

  const collapsedElementsRef = useRef<Set<string>>(
    deserializeCollapsedElements(
      readFromStorage<LayerTreeCollapsedState>(collapsedStateKey)
    )
  )

  const getCollapsedElements = useCallback(
    () => collapsedElementsRef.current,
    []
  )

  const setCollapsedElements = useCallback(
    (value: Set<string>) => {
      collapsedElementsRef.current = value
      writeToStorage(collapsedStateKey, {
        collapsedElements: Array.from(value).map(n => n.toString()),
      })
    },
    [collapsedStateKey]
  )

  const contextValue = useMemo(
    () => ({
      getScrollOffset,
      setScrollOffset,
      getCollapsedElements,
      setCollapsedElements,
    }),
    [
      getScrollOffset,
      setScrollOffset,
      getCollapsedElements,
      setCollapsedElements,
    ]
  )

  return <Provider value={contextValue}>{children}</Provider>
}

export const useLayerTreePersistentState = () => use(Provider)
